import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Table, Button, Form, InputGroup, Card, Spinner, Alert, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../../axiosConfig";

export default function EchipamenteList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 20;

  const navigate = useNavigate();

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/contor/echipamente", {
        params: {
          search: search.trim() || undefined,
          skip: (page - 1) * limit,
          limit: limit,
        },
      });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Eroare la preluarea echipamentelor.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchList, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Sigur doriți să ștergeți acest echipament? Toate sesizările legate de el ar putea deveni orfane sau ștergerea poate eșua dacă există referințe.")) {
      return;
    }
    try {
      await api.delete(`/contor/echipamente/${id}`);
      toast.success("Echipamentul a fost șters.");
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Eroare la ștergerea echipamentului.");
    }
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1 text-dark fw-bold">Echipamente</h2>
          <p className="text-muted mb-0">Evidența serializată a echipamentelor clienților și detalii contracte/contoare</p>
        </div>
        <Button
          variant="dark"
          onClick={() => navigate("/contor/echipamente/new")}
          className="shadow-sm px-4 py-2"
        >
          + Adaugă Echipament
        </Button>
      </div>

      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-3">
          <Form onSubmit={(e) => { e.preventDefault(); fetchList(); }}>
            <Row className="g-2">
              <Col xs={12} md={6}>
                <InputGroup>
                  <Form.Control
                    placeholder="Caută după serie, cod echipament, model, client..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  />
                  <Button variant="outline-secondary" type="submit">
                    Caută
                  </Button>
                </InputGroup>
              </Col>
              {search && (
                <Col xs={12} md={2}>
                  <Button
                    variant="outline-danger"
                    onClick={() => { setSearch(""); setPage(1); }}
                    className="w-100"
                  >
                    Resetează
                  </Button>
                </Col>
              )}
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Serie</th>
                <th>Cod</th>
                <th>Model</th>
                <th>Client Utilizator</th>
                <th>Locație</th>
                <th>Grupă</th>
                <th>Inginer</th>
                <th>Filială/Branch</th>
                <th style={{ width: "180px" }} className="text-end">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-5">
                    <Spinner animation="border" className="me-2" />
                    Se încarcă...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-muted text-center py-5">
                    Nu s-a găsit niciun echipament înregistrat.
                  </td>
                </tr>
              ) : (
                items.map((it) => (
                  <tr key={it.bxid}>
                    <td className="fw-bold text-dark">{it.bxseria}</td>
                    <td>{it.bxcodech || <span className="text-muted">-</span>}</td>
                    <td>
                      <div>{it.part_name || "-"}</div>
                      {it.part_code && <small className="text-muted font-monospace">{it.part_code}</small>}
                    </td>
                    <td>
                      <div>{it.utilizator_name || "-"}</div>
                      {it.proprietar_name && it.proprietar_name !== it.utilizator_name && (
                        <small className="text-muted d-block">Proprietar: {it.proprietar_name}</small>
                      )}
                    </td>
                    <td>{it.bxlocatie || <span className="text-muted">-</span>}</td>
                    <td>
                      {it.grupa_cod ? (
                        <Badge bg="info" className="text-dark fw-normal">
                          {it.grupa_cod}
                        </Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{it.inginer_name || <span className="text-muted">-</span>}</td>
                    <td>{it.branch_name || <span className="text-muted">-</span>}</td>
                    <td className="text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => navigate(`/contor/echipamente/edit/${it.bxid}`)}
                      >
                        Editează
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(it.bxid)}
                      >
                        Șterge
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {total > limit && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted small">Total: {total} echipamente</span>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Anterior
            </Button>
            <span className="align-self-center px-2">Pagina {page}</span>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={page * limit >= total}
              onClick={() => setPage(page + 1)}
            >
              Următor
            </Button>
          </div>
        </div>
      )}
    </Container>
  );
}
